import { useTranslation } from "react-i18next";
import {
  MessageCircle, ArrowRight, HeartPulse, Stethoscope, HandHeart,
  Plane, Briefcase, Users, Baby, UserRound, Globe,
  MessagesSquare, Search, CalendarCheck, ShieldCheck, Languages, MapPin, Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/i18n/routing";
import { Seo } from "@/components/Seo";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { CTAButtons } from "@/components/CTAButtons";
import { LeadDialog } from "@/components/LeadDialog";
import { SectionHeading, Eyebrow } from "@/components/SectionHeading";
import { ServiceCard, LocationCard, StepCard } from "@/components/Cards";
import { LOCATIONS, HELP_SERVICES, COMMON_CONCERNS, waLink } from "@/data/site";

const HOME_STEPS = [
  { n: "01", icon: MessagesSquare, k: "s1" },
  { n: "02", icon: MapPin, k: "s2" },
  { n: "03", icon: Search, k: "s3" },
  { n: "04", icon: CalendarCheck, k: "s4" },
  { n: "05", icon: HandHeart, k: "s5" },
];
const WHO = [
  { icon: Plane, k: "tourists" }, { icon: Globe, k: "expats" }, { icon: Stethoscope, k: "international" },
  { icon: Briefcase, k: "business" }, { icon: Users, k: "families" }, { icon: UserRound, k: "elderly" },
  { icon: HeartPulse, k: "recovering" }, { icon: Baby, k: "parents" },
];
const TRUST_POINTS = [
  { icon: Languages, k: "trustEng" }, { icon: ShieldCheck, k: "trustIndep" },
  { icon: Stethoscope, k: "trustDocs" }, { icon: Plane, k: "trustTravel" },
  { icon: MapPin, k: "trustCities" }, { icon: MessageCircle, k: "trustWa" },
  { icon: HeartPulse, k: "trustNurse" },
];
const TRUST_LABELS = {
  trustEng: "English-speaking coordination",
  trustIndep: "Independent healthcare support",
  trustDocs: "Access to doctors, clinics and hospitals",
  trustTravel: "Support for travellers and expats",
  trustCities: "Multiple locations across Vietnam",
  trustWa: "WhatsApp assistance",
  trustNurse: "Nursing and recovery support",
};

const Home = () => {
  const { t } = useTranslation();
  const faqs = t("home.faqs", { returnObjects: true }) || [];

  return (
    <>
      <Seo
        exact
        title={t("home.seoTitle")}
        description={t("home.metaDescription")}
        path="/"
        ogTitle={t("home.ogTitle")}
        ogDescription={t("home.ogDescription")}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-mint vc-hero-glow">
        <div className="vc-container grid items-center gap-10 py-14 lg:grid-cols-5 lg:py-20">
          <Stagger className="max-w-2xl lg:col-span-3">
            <StaggerItem><Eyebrow>{t("home.eyebrow")}</Eyebrow></StaggerItem>
            <StaggerItem>
              <h1 className="mt-5 text-4xl font-extrabold leading-[1.05] tracking-tight text-[hsl(var(--vc-ink))] sm:text-5xl lg:text-6xl">
                {t("home.h1")}
              </h1>
            </StaggerItem>
            <StaggerItem>
              <p className="mt-4 text-lg font-semibold text-brand">{t("home.subheading")}</p>
            </StaggerItem>
            <StaggerItem>
              <p className="mt-3 text-base leading-relaxed text-[hsl(var(--vc-slate))]">{t("home.intro")}</p>
            </StaggerItem>
            <StaggerItem>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <LeadDialog
                  title={t("cta.getMedical")}
                  defaultType="medical"
                  source="home-hero"
                  trigger={
                    <Button className="bg-brand py-6 text-base font-bold text-white hover:opacity-90" data-testid="hero-get-support-btn">
                      {t("cta.getMedical")} <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  }
                />
                <Button asChild variant="outline" className="border-2 border-brand/25 bg-white py-6 text-base font-bold text-brand hover:bg-mint" data-testid="hero-whatsapp-btn">
                  <a href={waLink()} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="mr-2 h-5 w-5" /> {t("cta.whatsappUs")}
                  </a>
                </Button>
              </div>
            </StaggerItem>
            <StaggerItem>
              <p className="mt-6 text-xs font-semibold uppercase tracking-[0.14em] text-[hsl(var(--vc-slate))]" data-testid="hero-trust-line">
                {t("home.trustLine")}
              </p>
            </StaggerItem>
          </Stagger>

          <Reveal className="lg:col-span-2">
            <div className="relative mx-auto max-w-sm rounded-3xl border border-[hsl(var(--vc-line))] bg-white p-8 vc-shadow-card">
              <img
                src="/brand/doctorsvietnam-logo.png"
                alt="Doctors Vietnam"
                className="mx-auto h-40 w-40 object-contain"
                loading="eager"
              />
              <div className="mt-5 rounded-2xl bg-mint p-4 text-center">
                <p className="text-sm font-bold text-brand">{t("home.heroBadgeTitle")}</p>
                <p className="mt-1 text-xs text-[hsl(var(--vc-slate))]">{t("home.heroBadgeText")}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* HOW WE CAN HELP */}
      <section className="vc-container py-16 sm:py-20">
        <SectionHeading align="center" eyebrow={t("home.helpEyebrow")} title={t("home.helpTitle")} subtitle={t("home.helpSubtitle")} />
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {HELP_SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.04}>
              <ServiceCard icon={s.icon} to={s.to} title={s.title} text={s.text} cta={t("cta.learnMore")} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* COMMON HEALTH CONCERNS */}
      <section className="bg-mint py-16 sm:py-20" data-testid="home-common-concerns">
        <div className="vc-container">
          <SectionHeading align="center" eyebrow={t("home.concernsEyebrow")} title={t("home.concernsTitle")} subtitle={t("home.concernsIntro")} />
          <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {COMMON_CONCERNS.map((c, i) => (
              <Reveal key={c} delay={i * 0.02}>
                <div className="flex items-center gap-3 rounded-xl border border-[hsl(var(--vc-line))] bg-white p-3.5 vc-shadow-soft">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-mint text-brand"><HeartPulse className="h-4 w-4" /></span>
                  <span className="text-sm font-semibold text-[hsl(var(--vc-ink))]">{c}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
            <Link
              to="/common-health-problems-vietnam"
              className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white hover:opacity-90"
              data-testid="home-common-cta"
            >
              {t("home.concernsCta")} <ArrowRight className="h-4 w-4" />
            </Link>
            <p className="max-w-xl text-xs text-[hsl(var(--vc-slate))]" data-testid="home-emergency-notice">
              <Info className="mr-1 inline h-3.5 w-3.5 text-brand" /> {t("home.concernsEmergency")}
            </p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="vc-container py-16 sm:py-20">
        <SectionHeading align="center" eyebrow={t("home.howEyebrow")} title={t("home.howTitle")} subtitle={t("home.howSubtitle")} />
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {HOME_STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.05}><StepCard n={s.n} icon={s.icon} title={t(`steps.${s.k}t`)} text={t(`steps.${s.k}x`)} /></Reveal>
          ))}
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-white py-14 sm:py-16">
        <div className="vc-container">
          <SectionHeading align="center" eyebrow={t("home.trustEyebrow")} title={t("home.trustTitle")} subtitle={t("home.trustSubtitle")} />
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {TRUST_POINTS.map((p, i) => (
              <Reveal key={p.k} delay={i * 0.04}>
                <div className="flex items-center gap-3 rounded-xl border border-[hsl(var(--vc-line))] bg-mint p-4">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-white text-brand vc-shadow-soft"><p.icon className="h-4 w-4" /></span>
                  <span className="text-sm font-semibold text-[hsl(var(--vc-ink))]">{TRUST_LABELS[p.k]}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE HELP */}
      <section className="bg-mint py-16 sm:py-20">
        <div className="vc-container">
          <SectionHeading align="center" eyebrow={t("home.whoEyebrow")} title={t("home.whoTitle")} subtitle={t("home.whoSubtitle")} />
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {WHO.map((w, i) => (
              <Reveal key={w.k} delay={i * 0.04}>
                <div className="flex h-full items-center gap-3 rounded-xl border border-[hsl(var(--vc-line))] bg-white p-4 vc-shadow-soft">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-mint text-brand"><w.icon className="h-5 w-5" /></span>
                  <span className="text-sm font-semibold text-[hsl(var(--vc-ink))]">{t(`home.who.${w.k}`)}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LOCATIONS */}
      <section className="vc-container py-16 sm:py-20">
        <SectionHeading align="center" eyebrow={t("home.locEyebrow")} title={t("home.locTitle")} subtitle={t("home.locSubtitle")} />
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {LOCATIONS.map((l, i) => (
            <Reveal key={l.slug} delay={i * 0.04}>
              <LocationCard name={l.name} region={l.region} blurb={l.blurb} to={`/locations/${l.slug}`} />
            </Reveal>
          ))}
        </div>
        <p className="mt-5 text-center text-sm text-[hsl(var(--vc-slate))]">{t("notices.availability")}</p>
      </section>

      {/* NURSING FEATURE */}
      <section className="bg-footer py-16 text-white sm:py-20">
        <div className="vc-container grid items-center gap-10 lg:grid-cols-2">
          <Reveal>
            <div className="rounded-3xl border border-white/10 bg-white/5 p-10 vc-shadow-card">
              <span className="grid h-16 w-16 place-items-center rounded-2xl bg-[hsl(150_62%_45%)] text-[hsl(166_60%_9%)]"><HandHeart className="h-8 w-8" /></span>
              <p className="mt-6 text-xs font-bold uppercase tracking-[0.16em] text-white/50">Home care</p>
              <p className="mt-2 text-xl font-bold">Nursing support delivered where you're staying</p>
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <Eyebrow className="bg-white/10 text-[hsl(150_62%_60%)]">{t("home.nursingEyebrow")}</Eyebrow>
            <h2 className="mt-4 text-3xl font-extrabold tracking-tight sm:text-4xl">{t("home.nursingTitle")}</h2>
            <p className="mt-3 text-base leading-relaxed text-white/70">{t("home.nursingText")}</p>
            <Button asChild className="mt-7 bg-[hsl(150_62%_45%)] font-bold text-[hsl(166_60%_9%)] hover:bg-[hsl(150_62%_50%)]" data-testid="explore-nursing-btn">
              <Link to="/nursing">{t("cta.exploreNursing")} <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-mint py-16 sm:py-20">
        <div className="vc-container max-w-3xl">
          <SectionHeading align="center" eyebrow={t("home.faqEyebrow")} title={t("home.faqTitle")} />
          <Accordion type="single" collapsible className="mt-8" data-testid="home-faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-[hsl(var(--vc-line))]">
                <AccordionTrigger className="text-left text-base font-bold text-[hsl(var(--vc-ink))] hover:no-underline" data-testid={`home-faq-trigger-${i}`}>{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-[hsl(var(--vc-slate))]">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="vc-container py-16 sm:py-20">
        <div className="relative overflow-hidden rounded-3xl bg-brand px-6 py-12 text-center text-white vc-shadow-card sm:px-10 sm:py-16">
          <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">{t("home.finalTitle")}</h2>
          <p className="mx-auto mt-3 max-w-xl text-base text-white/85">{t("home.finalText")}</p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button asChild className="bg-white py-6 text-base font-bold text-brand hover:bg-white/90" data-testid="final-whatsapp-btn">
              <a href={waLink()} target="_blank" rel="noopener noreferrer"><MessageCircle className="mr-2 h-5 w-5" /> {t("cta.chatWhatsapp")}</a>
            </Button>
            <LeadDialog
              title={t("cta.getMedical")}
              defaultType="medical"
              source="home-final-cta"
              trigger={
                <Button variant="outline" className="border-2 border-white/40 bg-transparent py-6 text-base font-bold text-white hover:bg-white/10" data-testid="final-get-support-btn">
                  {t("cta.getMedical")}
                </Button>
              }
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
